'use client';
import { useAuthUserContext } from '@/contexts/authUserContext';
import { trackCustomEvent } from '@/lib/analytics';
import Link from 'next/link';
import React, {
  useState,
  useCallback,
  ReactElement,
  cloneElement,
  isValidElement,
  useEffect,
} from 'react';

interface AuthGuardProps {
  children: ReactElement;
  loginMessage?: string;
  onLoginClick?: () => void;
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  loginMessage = 'Login to continue',
  onLoginClick,
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const { isAuthenticated } = useAuthUserContext();

  // Handler for child button click
  const handleChildClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isAuthenticated) {
        e.preventDefault();
        e.stopPropagation();
        setShowPrompt(true);
        trackCustomEvent('auth_guard_prompt', {
          label: 'show_login_prompt',
          category: 'AuthGuard',
        });
      } else {
        // Try to call child's onClick if it exists
        const childOnClick = (children as any)?.props?.onClick;
        if (typeof childOnClick === 'function') {
          childOnClick(e);
        }
      }
    },
    [isAuthenticated, children],
  );

  useEffect(() => {
    // Close the prompt when clicking outside
    document.addEventListener(
      'click',
      () => {
        if (showPrompt) {
          setShowPrompt(false);
        }
      },
      { capture: true },
    );
  }, []);

  // Clone the child and override its onClick
  let childWithAuth = children;
  if (isValidElement(children)) {
    childWithAuth = cloneElement(children as React.ReactElement<any>, {
      onClick: handleChildClick,
    });
  }

  return (
    <>
      {childWithAuth}
      {showPrompt && (
        <div className='fixed bottom-10 left-0 w-full flex justify-center z-250'>
          <div
            className='bg-primary shadow-lg rounded-full sm:w-4/5 md:w-2/5 px-2 py-2 flex justify-center items-center gap-4 animate-slide-up'
            onClick={(e) => e.stopPropagation()}
          >
            <Link
              href='/auth/login'
              className='text-white px-4 py-2 rounded font-semibold transition'
              onClick={() => setShowPrompt(false)}
              prefetch={false}
            >
              {loginMessage}
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default AuthGuard;
